import React, { useEffect, useRef } from 'react'
import { AlertTriangle, Building2, CircleAlert, CircleCheck, Clock, Hotel, UserRound } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import ReviewRows, { ReviewRowsSkeleton } from '@/components/review/ReviewRows'
import { cn } from '@/lib/utils'
import type { PricingReview, ReviewConflict } from '@/types/review'

/**
 * THE MOMENT BEFORE A PRICING SAVE (docs/plans/2026-10-20-change-safety.md §5.17.1).
 *
 * The incident this exists for: a person who works for two businesses opened the wrong hotel and
 * saved a base rate of £40 for £99, and five linked rooms followed it on every future night. The
 * confirm he clicked through counted rooms; it never named the hotel, the business or the values.
 * So this dialog leads with WHERE (the server's title names the hotel, the business sits beneath it,
 * for everyone, R1.9), then WHAT (each changed value, now and after), then WHAT IT REACHES.
 *
 * DISPLAY ONLY. Every title, figure and sentence is the server's (R1.5, R1.6). This file owns only
 * the fixed chrome the spec gives the client: the column headings, "Cancel", "Back", "Close", the
 * loading title, the refused title and the conflict heading.
 *
 * The four states, decided by the props and nothing else:
 *   loading  `loading`, or no review and no errors: skeletons, no confirm.
 *   refused  no review, errors: the server's sentences, "Close" only, under the hotel and business
 *            the 422 names (`heading`).
 *   empty    `review.empty`: the server's "nothing changed" sentence, "Close" only. Never saves.
 *   ready    the rows; errors, when present, are a save that was refused and can be tried again.
 *
 * KEYBOARD AND FOCUS. Esc, the close button and a click outside all mean the secondary action:
 * "Back" when the review came from a form (the form reopens with the draft intact), else "Cancel".
 * None of them saves, and none of them works while a save is running. Enter confirms only an
 * ordinary save, and only once the review has landed: while it loads there is nothing to confirm,
 * so a second press of the form's Enter cannot save unread. A destructive confirm, or an undo that
 * would overwrite later changes, is "cautious": Enter does not confirm it and focus starts on the
 * secondary action (WAI-ARIA APG, alert dialog), so it takes a deliberate press of its own button.
 * Focus is trapped by the primitive and returns to whatever opened the dialog when it closes, when
 * that element is still on the page.
 */

/** What a refused review still names: the 422 carries the hotel's title and the business. */
export interface ReviewHeading {
  title: string
  businessName: string
}

export interface PricingReviewDialogProps {
  open: boolean
  /** null while loading. */
  review: PricingReview | null
  /** 422 sentences, the network sentence, or a refused save's sentences. */
  errors: string[] | null
  /**
   * The title and business a refused review names (every review 422 carries them). Without one, a
   * refusal (a network failure) is titled "Changes not saved".
   */
  heading?: ReviewHeading | null
  saving: boolean
  onConfirm: () => void
  onCancel: () => void
  /** Shows "Back" instead of "Cancel": it closes the review and reopens the form with the draft intact. */
  onBack?: () => void
  /**
   * Shows the loading state even when `errors` is set: a refused save whose review is being fetched
   * again (an undo that lost a race). Defaults to "no review and no errors yet".
   */
  loading?: boolean
  /** Anything a caller needs above the rows. The conflict panel is built in: pass `conflicts`. */
  aboveRows?: React.ReactNode
  /** Overrides every other label. */
  confirmLabel?: string
  /** An undo review's later changes (R3.3). Non-empty: the conflict panel shows and the dialog is cautious. */
  conflicts?: ReviewConflict[] | null
  /** The confirm label while there are conflicts ("Overwrite and undo"). */
  confirmOverwriteLabel?: string | null
}

type Phase = 'loading' | 'refused' | 'empty' | 'ready'

// Elements whose own Enter must win over the dialog's: pressing Enter on "Cancel" cancels.
const OWN_ENTER = 'button, a[href], input, textarea, select, [role="button"], [contenteditable="true"]'

export default function PricingReviewDialog({
  open,
  review,
  errors,
  saving,
  onConfirm,
  onCancel,
  onBack,
  heading,
  loading,
  aboveRows,
  confirmLabel,
  conflicts,
  confirmOverwriteLabel,
}: PricingReviewDialogProps) {
  const contentRef = useRef<HTMLDivElement>(null)
  const primaryRef = useRef<HTMLButtonElement>(null)
  const secondaryRef = useRef<HTMLButtonElement>(null)
  // Radix returns focus to a `Dialog.Trigger` on close, and this dialog has none: a page opens it
  // from its own Save button through the hook. Without this the focus would fall to <body> and a
  // keyboard user would start again from the top of the page. So the dialog keeps its opener.
  const openerRef = useRef<HTMLElement | null>(null)

  const isLoading = loading ?? (review === null && errors === null)
  const phase: Phase = isLoading ? 'loading' : review === null ? 'refused' : review.empty ? 'empty' : 'ready'

  const hasConflicts = (conflicts?.length ?? 0) > 0
  const cautious = phase === 'ready' && (review!.confirmTone === 'destructive' || hasConflicts)
  const canConfirm = phase === 'ready' && !saving
  const label =
    phase === 'ready'
      ? (confirmLabel ?? (hasConflicts && confirmOverwriteLabel ? confirmOverwriteLabel : review!.confirmLabel))
      : null

  // Dismissing never discards: a review opened from a form goes back to it.
  const dismiss = onBack ?? onCancel

  function focusTarget(): HTMLElement | null {
    if (phase === 'loading') return contentRef.current
    if (phase === 'ready' && !cautious) return primaryRef.current
    return secondaryRef.current
  }

  // Move focus when the state changes under it (the review lands, is fetched again, or a refused
  // save re-enables the confirm that disabling had blurred), but only from where the dialog itself
  // put it: never pull focus off a control the person moved to.
  useEffect(() => {
    const content = contentRef.current
    if (!open || !content) return
    const active = document.activeElement
    if (active === content || active === document.body || active === null || !content.contains(active)) {
      focusTarget()?.focus()
    }
    // focusTarget reads the same render's phase and caution; re-running on them is the point.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, phase, cautious, saving])

  function handleOpenChange(next: boolean) {
    if (!next && !saving) dismiss()
  }

  function confirm() {
    if (canConfirm) onConfirm()
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key !== 'Enter' || e.defaultPrevented || e.nativeEvent.isComposing) return
    if ((e.target as HTMLElement).closest(OWN_ENTER)) return
    if (!canConfirm || cautious) return
    e.preventDefault()
    confirm()
  }

  const blockWhileSaving = (e: Event) => {
    if (saving) e.preventDefault()
  }

  const hasDescription = phase === 'ready' || phase === 'empty' || (phase === 'refused' && !!heading)

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {/* 560px, not the 480px of every other dialog: the rows table needs four columns (spec X6). */}
      <DialogContent
        ref={contentRef}
        tabIndex={-1}
        className="outline-none sm:max-w-[560px]"
        onOpenAutoFocus={(e) => {
          // Runs before focus moves, so the active element is still whatever opened the dialog.
          const opener = document.activeElement
          openerRef.current = opener instanceof HTMLElement && opener !== document.body ? opener : null
          e.preventDefault()
          ;(focusTarget() ?? contentRef.current)?.focus()
        }}
        onCloseAutoFocus={(e) => {
          const opener = openerRef.current
          openerRef.current = null
          if (opener?.isConnected) {
            e.preventDefault()
            opener.focus()
          }
        }}
        onEscapeKeyDown={blockWhileSaving}
        onInteractOutside={blockWhileSaving}
        onKeyDown={handleKeyDown}
        data-review-state={phase}
        {...(hasDescription ? {} : { 'aria-describedby': undefined })}
      >
        <DialogHeader>
          <Identity phase={phase} review={review} heading={heading ?? null} />
        </DialogHeader>

        <DialogBody className="space-y-4" aria-busy={phase === 'loading' || undefined}>
          {errors && errors.length > 0 && <ErrorPanel messages={errors} />}
          {phase === 'ready' && hasConflicts && <ConflictPanel conflicts={conflicts!} />}
          {phase !== 'loading' && aboveRows}

          {phase === 'loading' && <ReviewRowsSkeleton />}

          {phase === 'empty' && (
            <div
              role="status"
              className="page-transition rounded-xl border border-dashed border-border bg-white p-10 text-center shadow-sm"
            >
              <CircleCheck className="mx-auto h-8 w-8 text-muted-foreground/40" aria-hidden="true" />
              <p className="mt-3 text-sm font-medium text-foreground">{review!.emptyMessage}</p>
            </div>
          )}

          {phase === 'ready' && (
            // One reveal, as the review replaces its skeleton: the motion system's 150ms fade,
            // which the stylesheet already switches off under reduced motion.
            <div className="page-transition space-y-4">
              {review!.rows.length > 0 && <ReviewRows rows={review!.rows} />}
              {review!.warnings.length > 0 && <WarningPanel messages={review!.warnings} />}
            </div>
          )}
        </DialogBody>

        <DialogFooter>
          {phase === 'ready' || phase === 'loading' ? (
            <>
              <Button
                ref={secondaryRef}
                type="button"
                variant="ghost"
                className="rounded-lg"
                onClick={dismiss}
                disabled={saving}
              >
                {onBack ? 'Back' : 'Cancel'}
              </Button>
              {phase === 'ready' ? (
                <Button
                  ref={primaryRef}
                  type="button"
                  variant={review!.confirmTone === 'destructive' ? 'destructive' : 'default'}
                  className="rounded-lg"
                  onClick={confirm}
                  disabled={saving}
                  aria-busy={saving || undefined}
                >
                  {label}
                </Button>
              ) : (
                <Skeleton
                  className="h-10 w-32 rounded-lg motion-reduce:animate-none"
                  data-testid="review-confirm-skeleton"
                />
              )}
            </>
          ) : (
            <Button ref={secondaryRef} type="button" variant="outline" className="rounded-lg" onClick={dismiss}>
              Close
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Where the change lands. The hotel tile anchors the header; the server's title names the hotel and
 * the business sits beneath it on every review, whether or not the person belongs to two (R1.9).
 */
function Identity({
  phase,
  review,
  heading,
}: {
  phase: Phase
  review: PricingReview | null
  heading: ReviewHeading | null
}) {
  if (phase === 'refused' && !heading) {
    return <DialogTitle className="leading-snug">Changes not saved</DialogTitle>
  }
  const named = phase === 'refused' ? heading : review

  return (
    <div className="flex items-start gap-3">
      <div
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary"
        aria-hidden="true"
      >
        <Hotel className="h-5 w-5" />
      </div>
      <div className="min-w-0 flex-1 space-y-1.5 pt-0.5">
        {phase === 'loading' || !named ? (
          <>
            <DialogTitle className="sr-only">Checking your changes</DialogTitle>
            <Skeleton className="h-5 w-[70%] motion-reduce:animate-none" aria-hidden="true" />
            <Skeleton className="h-3.5 w-[40%] motion-reduce:animate-none" aria-hidden="true" />
          </>
        ) : (
          <>
            <DialogTitle className="leading-snug [overflow-wrap:anywhere]">{named.title}</DialogTitle>
            <DialogDescription className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Building2 className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              <span className="min-w-0 [overflow-wrap:anywhere]">{named.businessName}</span>
            </DialogDescription>
          </>
        )}
      </div>
    </div>
  )
}

function ErrorPanel({ messages }: { messages: string[] }) {
  return (
    <div role="alert" className="flex items-start gap-2.5 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2.5">
      <CircleAlert className="mt-0.5 h-4 w-4 shrink-0 text-rose-500" aria-hidden="true" />
      <ul className="min-w-0 space-y-1">
        {messages.map((message, i) => (
          <li key={i} className="text-sm text-rose-700 [overflow-wrap:anywhere]">
            {message}
          </li>
        ))}
      </ul>
    </div>
  )
}

function WarningPanel({ messages }: { messages: string[] }) {
  return (
    <div className="flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" aria-hidden="true" />
      <ul className="min-w-0 space-y-1" data-review-entry-warnings="">
        {messages.map((message, i) => (
          <li key={i} className="text-xs font-medium text-amber-800 [overflow-wrap:anywhere]">
            {message}
          </li>
        ))}
      </ul>
    </div>
  )
}

/**
 * What changed after the entry an undo would reverse (R3.3): each setting, the value it holds now,
 * and who changed it when. The undo goes ahead only through the overwrite confirm, so this panel
 * sits above the rows, where it is read first.
 */
function ConflictPanel({ conflicts }: { conflicts: ReviewConflict[] }) {
  return (
    <section
      aria-labelledby="review-conflicts-heading"
      className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5"
    >
      <h3 id="review-conflicts-heading" className="flex items-center gap-2 text-sm font-semibold text-amber-800">
        <AlertTriangle className="h-4 w-4 shrink-0 text-amber-500" aria-hidden="true" />
        These settings changed after this entry
      </h3>
      <ul className="mt-2 divide-y divide-amber-200/70">
        {conflicts.map((c) => (
          <li key={c.key} className="py-2 first:pt-0 last:pb-0" data-review-conflict={c.key}>
            <div className="flex items-baseline justify-between gap-3">
              <span className={cn('min-w-0 text-sm font-medium text-foreground', '[overflow-wrap:anywhere]')}>
                {c.label}
              </span>
              <span className="shrink-0 text-sm font-semibold tabular-nums text-foreground">{c.current}</span>
            </div>
            <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
              <span className="inline-flex min-w-0 items-center gap-1">
                <UserRound className="h-3 w-3 shrink-0" aria-hidden="true" />
                <span className="[overflow-wrap:anywhere]">{c.changedBy}</span>
              </span>
              <span className="inline-flex items-center gap-1">
                <Clock className="h-3 w-3 shrink-0" aria-hidden="true" />
                {c.changedAt}
              </span>
            </div>
          </li>
        ))}
      </ul>
    </section>
  )
}
